import { MomentumModal } from "@circle-react-shared/ModalsV2/MomentumModal";
import { workflowsApi } from "@circle-react/api/workflowsApi";
import { ModalManager } from "@circle-react/components/Layout/ModalManagerProvider";
import { useModal } from "@ebay/nice-modal-react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import type { TemplatesList } from "@/react/components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import { Icon } from "@/react/components/shared/Icon";
import { ActionButton } from "@/react/components/shared/ModalsV2/MomentumModal/ActionButton";
import { ShareNotice } from "@/react/components/shared/ModalsV2/MomentumModal/ShareNotice";
import { workflowsSettingsPaths } from "@/react/helpers/urlHelpers/workflowsSettingsPaths";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import { WorkflowsMomentumModalV4 } from "./WorkflowsMomentumModalV4";

export const WorkflowsMomentumModal = ModalManager.create(
  ({ templateKey }: { templateKey: string }) => {
    const modal = useModal();
    const navigate = useNavigate();
    const { isCircleV4 } = useCircleV4();
    const { data, isPending } = useQuery<TemplatesList>({
      queryKey: ["workflow-templates", "ai_workflows"],
      queryFn: () =>
        workflowsApi.templates({
          params: {
            category: "ai_workflows",
          },
        }),
    });

    const handleCreateWorkflow = () => {
      const workflowId = data?.records?.find(
        template => template?.key === templateKey,
      )?.id;
      if (workflowId) {
        void navigate({
          to: "/settings/workflows/new/preview/$id",
          params: { id: workflowId },
        });
      } else {
        void navigate({ to: workflowsSettingsPaths.new() });
      }
      void modal.hide();
    };

    if (isCircleV4) {
      return (
        <WorkflowsMomentumModalV4
          isOpen={modal.visible}
          isLoading={isPending}
          templateKey={templateKey}
          onClose={modal.hide}
          onCreateWorkflow={handleCreateWorkflow}
        />
      );
    }

    return (
      <MomentumModal
        isOpen={modal.visible}
        onClose={modal.hide}
        title={t(`workflows_v2.momentum_modal.${templateKey}.title`)}
        description={t(
          `workflows_v2.momentum_modal.${templateKey}.description`,
        )}
        isLoading={isPending}
        data-testid="reported-post-momentum-modal"
      >
        <ShareNotice
          title={t(`workflows_v2.momentum_modal.${templateKey}.notice.title`)}
          description={t(
            `workflows_v2.momentum_modal.${templateKey}.notice.description`,
          )}
        >
          <ActionButton
            iconEl={<Icon type="20-zap" size={20} />}
            onClick={handleCreateWorkflow}
            disabled={isPending}
            data-testid="create-moderation-workflow-button"
          >
            {t(`workflows_v2.momentum_modal.${templateKey}.notice.button`)}
          </ActionButton>
        </ShareNotice>
      </MomentumModal>
    );
  },
);
